import { useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { toPlain, typeOf } from "@/lib/fieldCodec";

const cell = (v) => {
  const t = typeOf(v);
  if (t === "null") return "";
  if (t === "timestamp") return v.toDate().toISOString();
  if (["map", "array", "geopoint", "reference"].includes(t)) return JSON.stringify(toPlain(v));
  return String(v);
};
const esc = (s) => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

const toCsv = (rows) => {
  const keys = ["id", ...Array.from(new Set(rows.flatMap((r) => Object.keys(r)))).filter((k) => k !== "id").sort()];
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(cell(r[k]))).join(","))].join("\n");
};

const download = (name, text, mime) => {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export function ExportMenu({ path, shown }) {
  const [busy, setBusy] = useState(false);
  const run = async (scope, fmt) => {
    setBusy(true);
    try {
      const rows = scope === "all" ? await adapter.listAll(path) : shown;
      const base = `${path.replace(/\//g, "_")}_${scope}_${new Date().toISOString().slice(0, 10)}`;
      if (fmt === "csv") download(`${base}.csv`, toCsv(rows), "text/csv");
      else download(`${base}.json`, JSON.stringify(rows.map(toPlain), null, 2), "application/json");
      toast.success(`Exported ${rows.length} documents`);
    } catch (e) { toast.error(e.code === "permission-denied" ? "No read access" : e.message); }
    finally { setBusy(false); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-9 gap-1" disabled={busy} data-testid="explorer-export-btn"><Icons.Download className="h-4 w-4" /> {busy ? "Exporting…" : "Export"}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="text-xs">Shown results ({shown.length})</DropdownMenuLabel>
        <DropdownMenuItem disabled={!shown.length} onClick={() => run("shown", "csv")} data-testid="export-shown-csv">CSV</DropdownMenuItem>
        <DropdownMenuItem disabled={!shown.length} onClick={() => run("shown", "json")} data-testid="export-shown-json">JSON</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs">Whole collection</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => run("all", "csv")} data-testid="export-all-csv">CSV</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run("all", "json")} data-testid="export-all-json">JSON</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
